import React, { useState, useRef, useEffect } from 'react';
import GoogleBusinessProfile from '../../../components/GoogleBusinessProfile/GoogleBusinessProfile';

import { authSelector } from '../../../store/slices/auth';
import { selectedClientSelector } from '../../../store/slices/selectedClient';
import { useSelector } from 'react-redux';
import { allowedEmails } from '../../../utils';
import { getGoogleProfileInfo } from '../../../components/GoogleBusinessProfile/services/index';
import './style.css';

const GoogleBusinessProfileSection = () => {
  const { userInfo } = useSelector(authSelector);
  const { selectedClient } = useSelector(selectedClientSelector);
  const currentEmail = userInfo?.email;
  const currentRole = userInfo?.role;
  const [isEditing, setIsEditing] = useState(false);
  const [showQualityAgent, setShowQualityAgent] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const googleBusinessProfileRef = useRef();

  const handleEditToggle = () => {
    if (isEditing) {
      // Si estamos cancelando la edición, limpiar campos y recargar datos
      // Necesitamos acceder al componente hijo para limpiar el estado
      if (
        googleBusinessProfileRef.current &&
        googleBusinessProfileRef.current.cancelEdit
      ) {
        googleBusinessProfileRef.current.cancelEdit();
      }
    }
    setIsEditing(!isEditing);
  };

  // Cargar datos del perfil de Google Business Profile
  useEffect(() => {
    const loadProfileData = async () => {
      if (selectedClient?.id) {
        try {
          const profileInfo = await getGoogleProfileInfo(selectedClient.id);
          setProfileData(profileInfo.data);
        } catch (error) {
          console.error('Error loading Google profile info:', error);
          setProfileData(null);
        }
      }
    };

    loadProfileData();
  }, [selectedClient?.id]);

  // Función para calcular el porcentaje de optimización del perfil
  const calculateOptimizationPercentage = () => {
    try {
      if (!profileData) return 0;

      const fields = [
        // Campos básicos (peso: 10% cada uno)
        {
          name: 'description',
          value: profileData.profile?.description,
          weight: 10
        },
        // Campos adicionales (peso: 10% cada uno)
        {
          name: 'serviceArea',
          value: profileData.serviceArea?.places?.placeInfos?.length > 0,
          weight: 10
        },
        {
          name: 'reviewFrequency',
          value: profileData.reviews_info?.totalReviewCount > 1,
          weight: 10
        },
        {
          name: 'googleRating',
          value: profileData.reviews_info?.totalReviewCount > 4,
          weight: 10
        },
        {
          name: 'serviceList',
          value: profileData.serviceItems?.length > 0,
          weight: 10
        },
        {
          name: 'serviceDescription',
          value:
            profileData.serviceItems &&
            profileData.serviceItems.length > 0 &&
            profileData.serviceItems[0]?.label?.length > 0,
          weight: 10
        },
        {
          name: 'reviewReplies',
          value: profileData.reviews_info?.totalReviewCount > 1,
          weight: 10
        },
        {
          name: 'numberOfImages',
          value: profileData.images_info?.totalMediaItemCount >= 3,
          weight: 10
        },
        {
          name: 'postsFrequency',
          value: profileData.posts_info?.posts_frecuency > 3,
          weight: 10
        },
        {
          name: 'postKeywordOptimization',
          value: profileData.posts_info?.posts_keywords?.length > 1,
          weight: 10
        }
      ];

      let totalScore = 0;
      let totalWeight = 0;

      fields.forEach((field) => {
        let isCompleted = false;

        try {
          // Manejar diferentes tipos de valores de manera segura
          if (typeof field.value === 'string') {
            isCompleted = field.value && field.value.trim().length > 0;
          } else if (typeof field.value === 'number') {
            isCompleted = field.value > 0;
          } else if (typeof field.value === 'boolean') {
            isCompleted = field.value === true;
          } else if (Array.isArray(field.value)) {
            isCompleted = field.value.length > 0;
          } else {
            isCompleted = !!field.value;
          }
        } catch (error) {
          console.warn(`Error evaluating field ${field.name}:`, error);
          isCompleted = false;
        }

        if (isCompleted) {
          totalScore += field.weight;
        }
        totalWeight += field.weight;
      });

      return Math.round((totalScore / totalWeight) * 100);
    } catch (error) {
      console.error('Error calculating optimization percentage:', error);
      return 0; // Retornar 0 en caso de error
    }
  };

  const optimizationPercentage = calculateOptimizationPercentage();

  return (
    <div className="card" id="posts-section">
      <div className="card-header pd-y-15 pd-x-20 d-flex align-items-center justify-content-between">
        <h6 className="tx-semibold mg-b-0">Google Business Profile</h6>
        <div className="d-flex gap-2">
          {/* Botones de acción */}
          {(currentRole === 'superAdmin' ||
            allowedEmails.includes(currentEmail)) && (
            <>
              <button
                className="btn  btn-white no-hover-text me-2"
                onClick={handleEditToggle}>
                {isEditing ? 'Cancel Edit' : 'Edit GBP'}
              </button>
              <button
                className="btn  btn-primary"
                onClick={() => {
                  if (
                    googleBusinessProfileRef.current &&
                    googleBusinessProfileRef.current.handleShowQualityAgent
                  ) {
                    googleBusinessProfileRef.current.handleShowQualityAgent();
                  }
                }}
                disabled={isEditing}
                aria-label="Toggle Agent">
                Agent
              </button>
            </>
          )}
        </div>
      </div>
      <div className="pd-2" style={{ overflowY: 'hidden' }}>
        <GoogleBusinessProfile
          isEditing={isEditing}
          setIsEditing={setIsEditing}
          showQualityAgent={showQualityAgent}
          setShowQualityAgent={setShowQualityAgent}
          handleShowQualityAgentRef={googleBusinessProfileRef}
          optimizationPercentage={optimizationPercentage}
        />
      </div>
    </div>
  );
};

export default GoogleBusinessProfileSection;
